import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ScrollView, StyleSheet, Text, View } from "react-native";

import {
  fonts,
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export function DataTable<TData>({
  columns,
  data,
  emptyMessage = "No records yet.",
}: {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  emptyMessage?: string;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <ScrollView horizontal contentContainerStyle={stylesheet.scrollContent}>
      <View style={stylesheet.table}>
        {table.getHeaderGroups().map((group) => (
          <View key={group.id} style={stylesheet.headerRow}>
            {group.headers.map((header) => (
              <View key={header.id} style={stylesheet.cell}>
                <Text style={stylesheet.headerText}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </Text>
              </View>
            ))}
          </View>
        ))}
        {table.getRowModel().rows.length === 0 ? (
          <View style={stylesheet.emptyRow}>
            <Text style={stylesheet.emptyText}>{emptyMessage}</Text>
          </View>
        ) : (
          table.getRowModel().rows.map((row) => (
            <View key={row.id} style={stylesheet.row}>
              {row.getVisibleCells().map((cell) => (
                <View key={cell.id} style={stylesheet.cell}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </View>
              ))}
            </View>
          ))
        )}
      </View>
    </ScrollView>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    scrollContent: { minWidth: "100%" },
    table: {
      minWidth: 720,
      flex: 1,
      overflow: "hidden",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.l,
      backgroundColor: colors.background,
    },
    headerRow: {
      minHeight: 42,
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.surface,
    },
    row: {
      minHeight: 54,
      flexDirection: "row",
      alignItems: "center",
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    cell: { minWidth: 130, flex: 1, paddingHorizontal: spacing.m },
    headerText: {
      color: colors.heading,
      fontFamily: fonts.semibold,
      fontSize: 12,
    },
    emptyRow: {
      minHeight: 90,
      alignItems: "center",
      justifyContent: "center",
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    emptyText: {
      color: colors.mutedForeground,
      fontFamily: fonts.regular,
      fontSize: 13,
    },
  });
